import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { LogLevel, ToastType } from "../enums";
import {
  isMobile,
  isTablet,
  osName,
  isDesktop,
  browserVersion,
  browserName,
  deviceType,
  osVersion,
} from "react-device-detect";
import { environment } from "../config/environment";

const api: AxiosInstance = axios.create({
  timeout: 10000,
});

const appendDeviceInfoInRequestHeaders = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  if (config.headers) {
    config.headers["uo-device-type"] = deviceType;
    config.headers["uo-os"] = osName;
    config.headers["uo-os-version"] = osVersion;
    config.headers["uo-is-mobile"] = isMobile;
    config.headers["uo-is-tablet"] = isTablet;
    config.headers["uo-is-desktop"] = isDesktop;
    config.headers["uo-browser"] = browserName;
    config.headers["uo-browser-version"] = browserVersion;
  }
  return config;
};

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

/**
 * Reads the token directly from storage, bypassing the closure `userToken`
 * captured by the last setupInterceptors() call.
 *
 * Why: setupInterceptors is invoked from App's render body with whatever
 * `userToken` the auth context currently holds - it only picks up a
 * freshly-logged-in token once App itself re-renders with the updated
 * context value. Login.tsx writes the token to storage synchronously right
 * after login (before navigating to the dashboard), but the destination
 * page's own data-fetching effects can mount and fire before that
 * re-render happens, so the interceptor's closure is still holding the
 * pre-login (empty) token at the exact moment those requests go out - no
 * Authorization header is attached, the backend returns empty/zero data
 * with no error, and only a full page refresh (which re-reads storage from
 * scratch before anything mounts) shows the real data. Reading storage
 * directly here removes the dependency on React's render timing entirely.
 */
const getStoredToken = (): string | null => {
  try {
    const key = environment.appPreferencesPrefix + "userToken";
    return environment.useLocalStorage
      ? localStorage.getItem(key)
      : sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

// When a page fires several requests at once and the session is expired, every
// response comes back 401. This guard ensures the session-timeout handling
// (toast + logout + redirect) runs only ONCE instead of once per failed request.
let isHandlingUnauthorized = false;

const setupInterceptors = (
  userToken: string,
  logout: () => void,
  showLoader: () => void,
  hideLoader: () => void,
  showToast: (
    message: string,
    title?: string,
    type?: ToastType,
    autoClose?: number,
  ) => void,
  log: (level: LogLevel, message: string, ...args: any[]) => void,
) => {
  // Eject previous interceptors before adding new ones to prevent stacking
  if (requestInterceptorId !== null) api.interceptors.request.eject(requestInterceptorId);
  if (responseInterceptorId !== null) api.interceptors.response.eject(responseInterceptorId);

  requestInterceptorId = api.interceptors.request.use(
    async (config) => {
      const shouldIgnoreLoader = environment.skipLoaderRoutes.some(
        (route: string) => config.url?.startsWith(route),
      );
      if (!shouldIgnoreLoader) {
        showLoader();
      }

      if (config.url?.startsWith("/api/v1/auth")) {
        config.baseURL = environment.authApiBaseUrl;
      } else if (config.url?.startsWith("/api/v1/admin-profile")) {
        config.baseURL = environment.userApiBaseUrl;
      } else if (config.url?.startsWith("/api/v1/admin")) {
        config.baseURL = environment.adminApiBaseUrl;
      } else if (config.url?.startsWith("/api/v1/devotee")) {
        config.baseURL = environment.devoteeApiUrl;
      }

      const token = getStoredToken() || userToken;
      if (token) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = token;
      }

      config = appendDeviceInfoInRequestHeaders(config);

      return config;
    },
    (error) => {
      hideLoader();
      return Promise.reject(error);
    },
  );

  responseInterceptorId = api.interceptors.response.use(
    (response: AxiosResponse) => {
      hideLoader();
      log(LogLevel.DEBUG, "api", response);
      return response;
    },
    (error: any) => {
      log(LogLevel.ERROR, "api", error);
      hideLoader();
      if (error.response && error.response.data) {
        if (
          error.response.status === 401 &&
          error.config.url !== "/api/v1/auth/admin/logout"
        ) {
          // Only handle the first 401 in a burst; ignore the rest so the toast,
          // logout and redirect don't fire multiple times.
          if (!isHandlingUnauthorized) {
            isHandlingUnauthorized = true;
            logout();
            showToast("Session Timed Out", "Error", ToastType.ERROR);
            setTimeout(() => {
              window.location.href = "/";
            }, 2000);
          }
        } else {
          if (error.response.data.errorCode === "AUTH00002") {
            setTimeout(() => {
              window.location.href = "/forget-password";
            }, 1000);
          }
        }
      }
      return Promise.reject(error);
    },
  );
};

export default api;
export { setupInterceptors };
